/**
 * ChatContext — route-scoped, mounted only for /chat/* and /eva/* routes via ChatLayout
 * in App.tsx. Unmounts completely when the user navigates outside that route group.
 *
 * OWNS
 *   • Active chat identity: currentChatId, currentSessionHeader — the session the user is
 *     currently looking at (FRIDA and EVA chats share this for header display/editing).
 *   • Navigation actions: selectChatSession() (navigates to /chat/:id),
 *     deleteChatSession() (deletes and redirects for FRIDA chats).
 *
 * BOUNDARIES
 *   • Does NOT own chat messages, artifacts, streaming state, or scheduled jobs — those are
 *     local state inside ChatPage / ChatContainer.
 *   • Does NOT own the FRIDA navbar session list — Navbar fetches its own independent list.
 *   • Does NOT own the EVA session list — EvaListContainer manages its own local list,
 *     same pattern as Navbar. Only currentSessionHeader is shared here for header editing.
 *   • Does NOT own the analysts catalog — that lives in AnalystChatContext (to be migrated).
 *   • Scoped to /chat/* and /eva/* routes so it resets when the user leaves those groups.
 *
 * CONSUMERS
 *   • ChatPage / ChatContainer (reads currentChatId, currentSessionHeader; calls deleteChatSession)
 *   • ChatPanelHeader container (reads currentSessionHeader; calls setCurrentSessionHeader)
 *   • EditChatModal (reads/writes currentSessionHeader to update title in place after editing)
 *   • Navbar (reads currentChatId to highlight the active session)
 */
import { DarwinHeader } from "build-ts/common";
import { AnalystProto } from "build-ts/analyst";
import showToast from "hooks/useCustomToast";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { deleteChatSession as deleteChatSessionAPI } from "services/AgentChatService";
import { getAnalyst } from "services/AnalystService";

interface ChatContextType {
  // Current chat state
  currentChatId: string;
  setCurrentChatId: React.Dispatch<React.SetStateAction<string>>;
  currentSessionHeader: DarwinHeader | undefined;
  setCurrentSessionHeader: React.Dispatch<React.SetStateAction<DarwinHeader | undefined>>;

  // Cached full analyst proto
  analystProto: AnalystProto | null;
  setAnalystProto: (proto: AnalystProto | null) => void;
  /**
   * Returns the cached AnalystProto if its ID matches `analystId`;
   * otherwise fetches it, updates the cache, and returns it.
   */
  getOrFetchAnalyst: (analystId: string) => Promise<AnalystProto | null>;

  // Actions
  selectChatSession: (sessionId: string) => void;
  deleteChatSession: (sessionId: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Current chat state
  const [currentChatId, setCurrentChatId] = useState<string>("");
  const [currentSessionHeader, setCurrentSessionHeader] = useState<DarwinHeader | undefined>(undefined);

  // Cached full AnalystProto for the active chat session.
  const [analystProto, setAnalystProto] = useState<AnalystProto | null>(null);

  const getOrFetchAnalyst = useCallback(
    async (analystId: string): Promise<AnalystProto | null> => {
      if (analystProto?.header?.id === analystId) {
        return analystProto;
      }
      const result = await getAnalyst(analystId);
      if (result) {
        setAnalystProto(result);
      }
      return result ?? null;
    },
    [analystProto]
  );

  // Select a chat session and navigate to it
  const selectChatSession = useCallback(
    (sessionId: string) => {
      setCurrentChatId(sessionId);
      navigate(`/chat/${sessionId}`);
    },
    [navigate]
  );

  // Delete a FRIDA chat session and navigate away if it is the currently open one.
  // EVA session deletes are handled locally in EvaListContainer (same pattern as Navbar).
  // Navbar manages its own FRIDA session list and handles its own refresh after delete.
  const deleteChatSession = useCallback(
    async (sessionId: string) => {
      try {
        await deleteChatSessionAPI(sessionId);
        if (currentChatId === sessionId) {
          setCurrentChatId("");
          navigate("/chat/new");
        }
      } catch (error) {
        showToast("Failed to delete chat session", "", "error");
        console.error("Error deleting chat session:", error);
      }
    },
    [currentChatId, navigate]
  );

  // Extract chat ID from URL when location changes and reset session header for "new" chats
  useEffect(() => {
    const pathParts = location.pathname.split("/");

    // FRIDA chat routes
    if (pathParts[1] === "chat") {
      const chatIdFromPath = pathParts[2];

      if (chatIdFromPath && chatIdFromPath !== "new") {
        // Existing FRIDA chat session
        setCurrentChatId(chatIdFromPath);
        // header will be set by the chat loader
      } else {
        // New FRIDA chat: clear current chat id, session header, and analyst cache
        setCurrentChatId("");
        setCurrentSessionHeader(undefined);
        setAnalystProto(null);
      }
    }
  }, [location.pathname]);

  const value = useMemo<ChatContextType>(
    () => ({
      currentChatId,
      setCurrentChatId,
      currentSessionHeader,
      setCurrentSessionHeader,
      analystProto,
      setAnalystProto,
      getOrFetchAnalyst,
      selectChatSession,
      deleteChatSession,
    }),
    [
      currentChatId,
      currentSessionHeader,
      analystProto,
      setAnalystProto,
      getOrFetchAnalyst,
      selectChatSession,
      deleteChatSession,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatContextProvider");
  }
  return context;
};
